/**
 * RuleImpactPreview Component
 *
 * Displays the preview of how a scheduling rule change would affect
 * current production batches. Shows batch count and shift details.
 *
 * US-PPL-001 Phase 5: Scheduling Rules Configuration
 */

import { Badge } from '@/components/ui/badge'
import { Loader2, AlertTriangle, CheckCircle } from 'lucide-react'

export default function RuleImpactPreview({ preview, loading }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Calculating impact...
      </div>
    )
  }

  if (!preview) return null

  const { batches_checked = 0, batches_affected = 0, total_shift_days = 0, diffs = [] } = preview

  if (batches_affected === 0) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-green-700">
        <CheckCircle className="h-4 w-4" />
        No batches affected ({batches_checked} checked)
      </div>
    )
  }

  return (
    <div className="space-y-3 py-3">
      <div className="flex items-center gap-2 text-sm">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <span className="font-medium">{batches_affected} batch{batches_affected !== 1 ? 'es' : ''} affected</span>
        <Badge variant="outline" className="text-xs">
          {total_shift_days} total shift days
        </Badge>
      </div>

      {diffs.length > 0 && (
        <div className="max-h-40 overflow-y-auto rounded border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium">Batch</th>
                <th className="px-3 py-1.5 text-right font-medium">Shift</th>
                <th className="px-3 py-1.5 text-right font-medium">Conflicts</th>
              </tr>
            </thead>
            <tbody>
              {diffs.map((diff, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-1.5 font-mono">{diff.batch_code}</td>
                  <td className="px-3 py-1.5 text-right">
                    <span className={diff.nudge_days > 0 ? 'text-amber-600' : 'text-green-600'}>
                      {diff.nudge_days > 0 ? '+' : ''}{diff.nudge_days}d
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {diff.has_conflicts && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">conflict</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
